/**
 * AssessmentModal - Centered overlay modal for function assessment
 *
 * A guided, capability-first assessment experience that replaces the
 * side panel approach. Features tabbed navigation through:
 * 1. Criteria - Understanding CISA maturity levels
 * 2. Assessment - Answering capability questions
 * 3. Gap Analysis - Viewing current vs target
 *
 * Designed as a centered overlay with smooth animations.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, BookOpen, ClipboardCheck, TrendingUp, Loader2 } from 'lucide-react';
import FunctionHeader from './FunctionHeader';
import MaturityCriteriaPanel from './MaturityCriteriaPanel';
import CapabilityChecklist, { DerivedLevelCard } from './CapabilityChecklist';
import GapSummary from './GapSummary';
import LevelComparisonView from './LevelComparisonView';
import { MATURITY_LEVELS } from '../../../../config/zeroTrustConstants';
import './AssessmentModal.css';

const TABS = [
  { id: 'criteria', label: 'Criteria', icon: BookOpen },
  { id: 'assessment', label: 'Assessment', icon: ClipboardCheck },
  { id: 'gap', label: 'Gap Analysis', icon: TrendingUp }
];

export default function AssessmentModal({
  isOpen,
  onClose,
  functionCode,
  functionName,
  pillarCode,
  pillarName,
  description,
  criteria, // { TRADITIONAL: string, INITIAL: string, ... }
  questions = [],
  answers = {},
  currentLevel,
  targetLevel,
  derivedLevel,
  onAnswerChange,
  onSetCurrentLevel,
  onSetTargetLevel,
  onOverrideLevel,
  isLoading = false,
  isSaving = false
}) {
  const [activeTab, setActiveTab] = useState('criteria');
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setActiveTab('criteria');
      onClose?.();
    }, 200);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const goToNextTab = () => {
    const currentIdx = TABS.findIndex(t => t.id === activeTab);
    if (currentIdx < TABS.length - 1) {
      setActiveTab(TABS[currentIdx + 1].id);
    }
  };

  const goToPrevTab = () => {
    const currentIdx = TABS.findIndex(t => t.id === activeTab);
    if (currentIdx > 0) {
      setActiveTab(TABS[currentIdx - 1].id);
    }
  };

  // Calculate missing capabilities for gap analysis
  const missingCapabilities = React.useMemo(() => {
    if (!targetLevel || !questions.length) return [];

    const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);

    return questions
      .filter(q => {
        const levelIdx = MATURITY_LEVELS.findIndex(m => m.key === q.impliesLevel);
        const isInScope = levelIdx <= targetIdx;
        const isNotAchieved = answers[q.questionId]?.answer !== true;
        return isInScope && isNotAchieved;
      })
      .map(q => ({
        questionText: q.questionText,
        impliesLevel: q.impliesLevel,
        priority: getPriority(q.impliesLevel, targetLevel)
      }));
  }, [questions, answers, targetLevel]);

  if (!isOpen) return null;

  return (
    <div
      className={`am-backdrop ${isClosing ? 'am-backdrop--closing' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="am-title"
    >
      <div
        ref={modalRef}
        className={`am-modal ${isClosing ? 'am-modal--closing' : ''}`}
        tabIndex={-1}
      >
        {/* Close Button */}
        <button className="am-close-btn" onClick={handleClose} aria-label="Close">
          <X size={20} />
        </button>

        {/* Header */}
        <div className="am-header">
          <FunctionHeader
            functionCode={functionCode}
            functionName={functionName}
            pillarCode={pillarCode}
            description={description}
            currentLevel={currentLevel || derivedLevel}
            targetLevel={targetLevel}
            animated
          />

          {/* Tab Navigation */}
          <nav className="am-tabs">
            {TABS.map((tab, idx) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isCompleted = idx < TABS.findIndex(t => t.id === activeTab);

              return (
                <button
                  key={tab.id}
                  className={`
                    am-tab
                    ${isActive ? 'am-tab--active' : ''}
                    ${isCompleted ? 'am-tab--completed' : ''}
                  `.trim().replace(/\s+/g, ' ')}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="am-tab-number">{idx + 1}</span>
                  <Icon size={16} />
                  <span className="am-tab-label">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="am-content">
          {isLoading ? (
            <div className="am-loading">
              <Loader2 size={32} className="am-loading-spinner" />
              <span>Loading assessment data...</span>
            </div>
          ) : (
            <>
              {/* Criteria Tab */}
              {activeTab === 'criteria' && (
                <div className="am-tab-content am-tab-content--criteria">
                  <MaturityCriteriaPanel
                    criteria={criteria}
                    currentLevel={currentLevel || derivedLevel}
                    targetLevel={targetLevel}
                    selectionMode="none"
                    showProgressionHints
                    animated
                  />
                </div>
              )}

              {/* Assessment Tab */}
              {activeTab === 'assessment' && (
                <div className="am-tab-content am-tab-content--assessment">
                  <div className="am-assessment-layout">
                    <div className="am-assessment-main">
                      <CapabilityChecklist
                        questions={questions}
                        answers={answers}
                        onAnswerChange={onAnswerChange}
                        derivedLevel={derivedLevel}
                        showDerivedFeedback
                        showEvidenceFields
                        groupByLevel
                        animated
                      />
                    </div>

                    <aside className="am-assessment-sidebar">
                      <DerivedLevelCard
                        derivedLevel={derivedLevel || 'TRADITIONAL'}
                        onOverride={onOverrideLevel}
                        animated
                      />

                      {!targetLevel && (
                        <div className="am-set-target">
                          <h4>Set Your Target</h4>
                          <p>Choose where you want to be:</p>
                          <div className="am-target-options">
                            {MATURITY_LEVELS.filter(m => {
                              const derivedIdx = MATURITY_LEVELS.findIndex(l => l.key === derivedLevel);
                              const thisIdx = MATURITY_LEVELS.findIndex(l => l.key === m.key);
                              return thisIdx > derivedIdx;
                            }).map(level => (
                              <button
                                key={level.key}
                                className="am-target-option"
                                onClick={() => onSetTargetLevel?.(level.key)}
                                style={{ '--level-color': level.color }}
                              >
                                {level.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </aside>
                  </div>
                </div>
              )}

              {/* Gap Analysis Tab */}
              {activeTab === 'gap' && (
                <div className="am-tab-content am-tab-content--gap">
                  {targetLevel ? (
                    <GapSummary
                      currentLevel={currentLevel || derivedLevel || 'TRADITIONAL'}
                      targetLevel={targetLevel}
                      missingCapabilities={missingCapabilities}
                      functionName={functionName}
                      animated
                    />
                  ) : (
                    <div className="am-no-target">
                      <TrendingUp size={48} />
                      <h3>Set a target level first</h3>
                      <p>Go to the Assessment tab and set your target maturity level to see gap analysis.</p>
                      <button
                        className="am-go-assessment"
                        onClick={() => setActiveTab('assessment')}
                      >
                        Go to Assessment
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="am-footer">
          <div className="am-footer-left">
            {activeTab !== 'criteria' && (
              <button className="am-nav-btn am-nav-btn--prev" onClick={goToPrevTab}>
                <ChevronLeft size={18} />
                Previous
              </button>
            )}
          </div>

          <div className="am-footer-center">
            {isSaving && (
              <span className="am-saving">
                <Loader2 size={14} className="am-saving-spinner" />
                Saving...
              </span>
            )}
          </div>

          <div className="am-footer-right">
            {activeTab !== 'gap' ? (
              <button className="am-nav-btn am-nav-btn--next" onClick={goToNextTab}>
                Next
                <ChevronRight size={18} />
              </button>
            ) : (
              <button className="am-done-btn" onClick={handleClose}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Determine priority based on how far the level is from target
 */
function getPriority(impliesLevel, targetLevel) {
  const impliesIdx = MATURITY_LEVELS.findIndex(m => m.key === impliesLevel);
  const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);

  if (impliesIdx >= targetIdx - 1) return 'high';
  if (impliesIdx >= targetIdx - 2) return 'medium';
  return 'low';
}

/**
 * useAssessmentModal - Hook for controlling the modal
 */
export function useAssessmentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState({});

  const openModal = useCallback((props) => {
    setModalProps(props);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalProps({});
  }, []);

  return {
    isOpen,
    modalProps,
    openModal,
    closeModal
  };
}
